import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"
import LinkIcon from "@mui/icons-material/Link"
import Box from "@mui/material/Box"
import Stack from "@mui/material/Stack"
import Chip from "@mui/material/Chip"
import LaunchRoundedIcon from "@mui/icons-material/LaunchRounded"
import Lottie from "lottie-react"
import { PageSectionItem } from "../../../interface/PageSectionItem"
import { alpha, useTheme } from "@mui/material/styles"

interface PageSectionProps
{
  pageSection: PageSectionItem
}

export const PageSection = ({ pageSection }: PageSectionProps) => {
  const theme = useTheme()

  return(
    <Box sx={styles.content}>
      <Box sx={styles.panel(theme)}>
        <Stack spacing={1}>
          <Chip label="Onboarding Step" sx={styles.chip(theme)} />
          <Typography variant="h3" sx={styles.title}>{pageSection.title}</Typography>
          <Typography variant="body1" sx={styles.description(theme)}>
            {pageSection.description}
          </Typography>
        </Stack>

        <Box sx={styles.animationWrap(theme)}>
          <Lottie animationData={pageSection.animation} loop={false} autoPlay style={styles.lottie} />
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 1 }}>
          { pageSection.linkTitle && pageSection.linkUrl &&
            <Button onClick={() => window.open(pageSection.linkUrl, "_blank")} startIcon={<LinkIcon />} endIcon={<LaunchRoundedIcon />} variant="contained" sx={styles.primaryButton}>
              {pageSection.linkTitle}
            </Button> }

          { pageSection.linkUrlAlt && pageSection.linkTitleAlt &&
            <Button onClick={() => window.open(pageSection.linkUrlAlt, "_blank")} startIcon={<LinkIcon />} endIcon={<LaunchRoundedIcon />} variant="outlined" sx={styles.secondaryButton(theme)}>
              {pageSection.linkTitleAlt}
            </Button> }
        </Stack>
      </Box>
    </Box>
  )
}

const styles = {
  content: {
    padding: 0,
    width: "100%",
    display: "flex",
  },
  panel: (theme: any) => ({
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 1,
    borderRadius: "16px",
    padding: 2,
    background:
      theme.palette.mode === "dark"
        ? `linear-gradient(135deg, ${alpha("#11243b", 0.9)} 0%, ${alpha("#0c1727", 0.92)} 100%)`
        : `linear-gradient(135deg, ${alpha("#ffffff", 0.94)} 0%, ${alpha("#eaf3ff", 0.92)} 100%)`,
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.55)}`,
  }),
  chip: (theme: any) => ({
    width: "fit-content",
    borderRadius: "999px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.74),
    '& .MuiChip-label': {
      fontWeight: 700,
    },
  }),
  title: {
    fontWeight: 700,
    lineHeight: 1.05,
  },
  description: (theme: any) => ({
    maxWidth: 700,
    color: alpha(theme.palette.text.primary, 0.7),
  }),
  animationWrap: (theme: any) => ({
    marginTop: 2,
    marginBottom: 6,
    minHeight: 200,
    display: "grid",
    placeItems: "center",
    borderRadius: "14px",
    backgroundColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.03 : 0.6),
    border: `1px solid ${alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.06 : 0.45)}`,
  }),
  lottie: {
    width: 220,
    height: 220,
  },
  primaryButton: {
    width: "fit-content",
    borderRadius: "12px",
    boxShadow: "none",
  },
  secondaryButton: (theme: any) => ({
    width: "fit-content",
    borderRadius: "12px",
    borderColor: alpha(theme.palette.common.white, theme.palette.mode === "dark" ? 0.08 : 0.45),
  }),
}
